import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { getAuraLevel } from '../utils/auraUtils';
import axios from 'axios';
import { apiFetch, apiFetchWithToken, resolveAssetUrl } from '../utils/api';
import { clearAuthStorage, decodeJwtPayload, handleUserSwitch } from '../utils/auth';
import { refreshAccessToken } from '../utils/tokenRefresh';
import { AURA_CLIENT_HEADERS } from '../utils/auraClient';
import { fetchAndCacheSchoolLogo } from '../utils/schoolLogo';
import browserCacheService from '../services/BrowserCacheService';
import { flushBrowseEvents } from '../utils/browseEvents';
import { isPasswordValid, PASSWORD_RULE_MESSAGE } from '../utils/passwordPolicy';
import { setUser as setSentryUser, clearUser as clearSentryUser } from '../config/sentry';

const AuthContext = createContext(null);

/**
 * Fügt Standardwerte für auraPoints und auraLevel hinzu, falls sie fehlen
 */
const ensureAuraProperties = (userData) => {
  if (!userData) return null;
  
  // Extrahiere Aura-Daten aus allen möglichen Quellen
  const auraPoints = userData.auraPoints || 
    (userData.auraStats?.currentPoints) || 
    (userData.profile?.auraStats?.currentPoints) || 0;
  
  // auraLevel kann aus mehreren Quellen kommen oder wird berechnet
  const auraLevel = userData.auraLevel || 
    (userData.auraStats?.level) || 
    (userData.profile?.auraStats?.level) || 
    getAuraLevel(auraPoints);
  
  // Erstelle eine neue Kopie mit garantierten Aura-Eigenschaften
  return {
    ...userData,
    auraPoints,
    auraLevel
  };
};

// Bei einem Backend-Neustart (jeder Deploy) oder einer Proxy-Störung kommt statt JSON
// eine HTML-Fehlerseite (502/503/504). Weder deren Text noch ein Parserfehler
// ("JSON Parse error: Unrecognized token '<'") gehören ins Anmeldeformular.
const serverUnavailableMessage = (status) =>
  `Der Server ist gerade nicht erreichbar (Status ${status}). Bitte versuche es gleich noch einmal.`;

// Kontowechsel ohne Logout: das Gerät für die Pushes dem neuen Konto zuordnen. Erst
// aufrufen, wenn das neue Token im localStorage steht (der Service liest es dort).
// Fire-and-forget und dynamischer Import wie beim Logout (Tests mocken axios).
const reassignPushDevice = () => {
  import('../services/PushNotificationService')
    .then(({ default: pushService }) => pushService.registerForCurrentAccount())
    .catch(() => {});
};

const normalizeImageUrl = (value) => {
  if (value === null) return null;
  if (!value) return '';
  return resolveAssetUrl(value);
};

const stripBackendUrl = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'string') return value;
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (baseUrl && value.startsWith(baseUrl)) {
    return value.slice(baseUrl.length);
  }
  return value;
};

export const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(localStorage.getItem('token') || null);
  const [user, setUser] = useState(null);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [ltiContext, setLtiContext] = useState(null);
  const [isLtiUser, setIsLtiUser] = useState(false);

  // Sicherer Setter für den Benutzer, der auraPoints und auraLevel sicherstellt
  // ✅ FIX: Memoize setUserWithAura mit useCallback um Stabilität zu gewährleisten
  const setUserWithAura = useCallback((userData) => {
    if (!userData) {
      setUser(null);
      return;
    }
    
    const enhancedUser = ensureAuraProperties(userData);
    
    // NEU: LTI-Informationen prüfen und ergänzen
    if (ltiContext) {
      enhancedUser.ltiContextId = ltiContext;
      enhancedUser.isLtiUser = true;
      setIsLtiUser(true);
    }
    
    // Profilbild entfernt (ProfileEditPage setzt image UND avatar explizit auf null):
    // dann NICHT auf profile.avatar zurueckfallen - genau dieser Rueckfall holte das
    // geloeschte Bild sofort wieder in den Zustand (5. Sep 2026: "Profilbild loeschen
    // geht nicht", obwohl der Server es laengst entfernt hatte).
    const imageExplicitlyRemoved = enhancedUser.image === null && enhancedUser.avatar === null;
    const rawImage = imageExplicitlyRemoved
      ? ''
      : (enhancedUser.image || enhancedUser.avatar || enhancedUser.profile?.avatar || enhancedUser.profile?.image || '');
    const resolvedImage = normalizeImageUrl(rawImage);

    // Der Anzeigename liegt je nach Endpoint an unterschiedlichen Stellen: /auth/me liefert
    // ihn als `name`, die Registrierung dagegen nur als `displayName` im Profil
    // (DB-Feld: tenant_profiles.displayName). Ohne diese Normalisierung war das Namensfeld
    // im Profil-Setup direkt nach der Registrierung leer.
    const resolvedName = enhancedUser.name
      || enhancedUser.displayName
      || enhancedUser.profile?.displayName
      || enhancedUser.profile?.name
      || '';

    const normalizedUser = {
      ...enhancedUser,
      name: resolvedName,
      image: resolvedImage,
      avatar: resolvedImage,
      profile: enhancedUser.profile
        ? {
          ...enhancedUser.profile,
          avatar: resolvedImage,
          image: resolvedImage
        }
        : enhancedUser.profile
    };
    
    setUser(normalizedUser);
    
    // Aktualisiere auch localStorage
    localStorage.setItem('user', JSON.stringify(normalizedUser));
  }, [ltiContext]); // Dependencies: ltiContext

  // Initialisiere den Benutzer und Token aus dem localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedLtiContext = localStorage.getItem('ltiContextId');
    
    if (storedLtiContext) {
      setLtiContext(storedLtiContext);
      setIsLtiUser(true);
    }
    
    let hasToken = false;
    if (storedToken) {
      try {
        // Versuche, Token zu dekodieren, um seine Gültigkeit zu prüfen
        const tokenParts = storedToken.split('.');
        if (tokenParts.length !== 3) {
          console.error('Ungültiges Token-Format im localStorage');
          // Ungültigen Token entfernen
          localStorage.removeItem('token');
        } else {
          // Setze den Token im State und aktualisiere die Zeit
      setAuthToken(storedToken);
          localStorage.setItem('token_set_time', Date.now().toString());
          hasToken = true;
        }
      } catch (error) {
        console.error('Fehler beim Überprüfen des Tokens:', error);
      }
    } else {
      console.warn('⚠️ Kein Token im localStorage gefunden');
    }

    // Ein gespeichertes Konto ohne Token ist keine Anmeldung, sondern ein Rest (z. B.
    // ein Token mit kaputtem Format oder ein Stand aus der Zeit vor dem getUser-Schutz).
    // Bisher wurde es trotzdem hydriert: Die App hielt sich für angemeldet und schickte
    // bei JEDEM Start /legal/me, das Schullogo und register-device ohne Token los. Die
    // checkUserAuth-Prüfung unten greift nur mit Token, aufgeräumt hat es also niemand
    // (Sentry NODE-P, "Kein Token vorhanden", Okt 2026). Jetzt: abgemeldet, Reste weg.
    if (storedUser && !hasToken) {
      clearAuthStorage();
      setAuthToken(null);
      setLtiContext(null);
      setIsLtiUser(false);
      return;
    }

    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        
        // VERBESSERT: Stelle sicher, dass wichtige Profilfelder auf der Hauptebene verfügbar sind
        if (userData) {
          // Extrahiere kritische Felder aus dem Profil-Objekt auf die Hauptebene
          // Wir prüfen alle möglichen Quellen für die Daten
          const profile = userData.profile || {};
          
          const enhancedUser = {
            ...userData,
            name: userData.name || profile.name || profile.displayName || '',
            grade: userData.grade || profile.grade || '',
            age: userData.age || profile.age || '',
            bio: userData.bio || profile.bio || '',
            aboutMe: userData.aboutMe || profile.bio || profile.aboutMe || '',
            image: userData.image || profile.image || profile.avatar || '',
            // Initialisiere Skills-Arrays
            offers: userData.offers || [],
            seeks: userData.seeks || [],
            // Verfügbarkeit
            availability: userData.availability || profile.availability || {},
            // Stelle sicher, dass Aura-Daten einheitlich verfügbar sind
            auraPoints: userData.auraPoints || 
                      (userData.auraStats?.currentPoints) || 
                      (profile.auraStats?.currentPoints) || 0,
            auraLevel: userData.auraLevel || 
                     (userData.auraStats?.level) || 
                     (profile.auraStats?.level) || 1,
            // Speichere token-Referenz im User-Objekt für bessere Debugging-Möglichkeiten
            _hasToken: !!storedToken,
            // NEU: LTI-Kontext-Informationen, falls vorhanden
            ltiContextId: storedLtiContext || userData.ltiContextId,
            isLtiUser: !!storedLtiContext || userData.isLtiUser
          };
          
          // Extrahiere Skills, wenn vorhanden
          // Prüfe mehrere mögliche Quellen für Skills
          const offeredSkills = 
            (userData.offers && userData.offers.length > 0) ? userData.offers :
            (profile.offeredSkills && Array.isArray(profile.offeredSkills)) ? 
              profile.offeredSkills.map(skill => typeof skill === 'object' ? skill.name : skill) :
            (profile.offers && Array.isArray(profile.offers)) ? 
              profile.offers.map(skill => typeof skill === 'object' ? skill.name : skill) : 
            [];
            
          const soughtSkills = 
            (userData.seeks && userData.seeks.length > 0) ? userData.seeks :
            (profile.soughtSkills && Array.isArray(profile.soughtSkills)) ? 
              profile.soughtSkills.map(skill => typeof skill === 'object' ? skill.name : skill) :
            (profile.seeks && Array.isArray(profile.seeks)) ? 
              profile.seeks.map(skill => typeof skill === 'object' ? skill.name : skill) : 
            [];
          
          enhancedUser.offers = offeredSkills;
          enhancedUser.seeks = soughtSkills;
          
          setUserWithAura(enhancedUser);
          
          // Aktualisiere auch localStorage mit den erweiterten Daten
          localStorage.setItem('user', JSON.stringify(enhancedUser));
        } else {
          // Wenn kein Profil vorhanden ist, nutze die Daten unverändert
          setUserWithAura(userData);
        }
      } catch (error) {
        console.error('Fehler beim Parsen des gespeicherten Benutzers:', error);
      }
    }
  }, []);

  // Benutzer auf Basis eines vorhandenen Tokens prüfen
  useEffect(() => {
    const checkUserAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Timeout ist Pflicht: Ohne ihn blieb `loading` bei einem hängenden Request
          // (Schul-WLAN, Captive Portal) für immer true, der Provider rendert dann
          // keine Kinder und der Splash blendet nach 8 s aus -> weißer Bildschirm ohne
          // Ausweg. Nach dem Timeout bleibt das Token erhalten (kein 401), die App
          // rendert und getUser() in App.js versucht es erneut.
          const fetchMe = (bearerToken) => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/auth/me`, {
            headers: { ...AURA_CLIENT_HEADERS, Authorization: `Bearer ${bearerToken}` },
            timeout: 15000
          });

          let response;
          try {
            response = await fetchMe(token);
          } catch (meError) {
            if (meError.response?.status !== 401) throw meError;
            // Abgelaufen heißt nicht verloren: /auth/refresh erneuert ein Token bis
            // 7 Tage nach Ablauf. Bisher flog es beim Kaltstart sofort raus, die App
            // verlangte also nach jedem Ablauf eine neue Anmeldung, obwohl ein Refresh
            // gereicht hätte. Scheitert der Refresh, gilt der ursprüngliche 401.
            // Das Zeitlimit gilt auch hier (siehe oben, weißer Bildschirm). Läuft es ab,
            // ist das kein Urteil über das Token: wie ein Netzfehler, das Token bleibt.
            let freshToken;
            try {
              freshToken = await refreshAccessToken({ timeoutMs: 10000 });
            } catch (refreshError) {
              if (refreshError.name === 'TimeoutError') throw refreshError;
              throw meError;
            }
            setAuthToken(freshToken);
            response = await fetchMe(freshToken);
          }

          // ✅ FIX: Verwende setUserWithAura statt setUser um Aura-Properties zu garantieren
          // Hinweis: /me sendet User-Daten direkt (nicht in .user nested)
          const userData = response.data.user || response.data;

          // Schutz gegen "HTTP 200 mit Fehler-Body" aus der Tenant-Security-Middleware:
          // ohne id sind das keine Benutzerdaten, sondern ein verkleideter Fehler.
          if (!userData || !userData.id) {
            throw new Error('Antwort ohne User-ID von /auth/me');
          }

          setUserWithAura(userData);
          setIsAuthenticated(true);
          setSentryUser(userData); // nur die ID (Pseudonym), keine E-Mail

          // ✅ NEU: Prüfe ob Profil-Setup erforderlich ist (unvollständiges Profil)
          if (response.data.requiresProfileSetup) {
            setShowProfileSetup(true);
          }

          // LTI-Kontext laden, falls vorhanden
          const storedLtiContext = localStorage.getItem('ltiContextId');
          if (storedLtiContext) {
            setLtiContext(storedLtiContext);
            setIsLtiUser(true);
          }

        } catch (error) {
          // NUR bei 401 abmelden. Frueher flog der Token bei JEDEM Fehler raus - also auch
          // bei 400 (fehlende SchoolId), 403 (Tenant), 503 (DB) und bei einem simplen
          // Netzwerkhaenger. Ein kurzer Aussetzer beim App-Start bedeutete damit Logout.
          const status = error.response?.status;
          if (status === 401) {
            console.warn('Token abgelaufen oder ungueltig - melde ab');
            // Vollständig abmelden: Nur das Token zu entfernen ließ `user` (State und
            // localStorage) und den alten authToken-State stehen. Der alte State landete
            // dann beim nächsten Login im getUser-Aufruf, bekam 401 und löschte das
            // frische Token gleich wieder.
            clearAuthStorage();
            setAuthToken(null);
            setUser(null);
          } else {
            // Bewusst console.warn, NICHT console.error: Dies ist der oben
            // beschriebene, gewollte Fallback (Netzwerkhaenger/400/403/5xx beim
            // Start -> Token bleibt erhalten, App rendert, getUser() in App.js
            // versucht es erneut). Ein console.error wuerde ueber
            // setupConsoleErrorTracking() als Sentry-Issue landen und einen
            // sauber behandelten, nicht-fatalen Zustand als Fehler melden
            // (Sentry JAVASCRIPT-REACT-KA, 45 Events aus iOS-WKWebView).
            console.warn('Token-Pruefung fehlgeschlagen (Token bleibt erhalten)', {
              status: status || 'kein Status (Netzwerkfehler)',
              message: error.message
            });
          }
        }
      }
      setLoading(false);
    };

    checkUserAuth();
  }, []); // ✅ FIX: Leere Dependencies - nur beim Mount prüfen, nicht bei setUserWithAura-Änderungen

  // Registrierung - nur Account erstellen
  // 🎫 invitationCode ist optional - wenn vorhanden, wird die Schule aus der Invitation verwendet
  const register = async (email, password, username = '', invitationCode = null, privacyAccepted = false) => {
    try {


      // Lokale Validierung
      if (!email || !password) {
        throw new Error('E-Mail und Passwort sind erforderlich');
      }

      // Username-Validierung
      const finalUsername = username?.trim() || email.split('@')[0];

      // Passwort-Validierung: zentrale "Länge-zuerst"-Regel (utils/passwordPolicy),
      // identisch zur Live-Prüfung in AuthPage und zur Backend-Regel (validation.js).
      // Vorher stand hier eine veraltete, strengere Kopie (Groß+Klein+Zahl+
      // Sonderzeichen aus nur @$!%*?&), die 12-Zeichen-Passphrasen und Bindestrich-
      // Sonderzeichen ablehnte, obwohl AuthPage sie als gültig anzeigte -> Registrierung
      // scheiterte trotz grüner Haken (Sentry JAVASCRIPT-REACT-HJ).
      if (!isPasswordValid(password)) {
        throw new Error(PASSWORD_RULE_MESSAGE);
      }

      const requestData = {
        email: email.toLowerCase().trim(),
        password: password,
        username: finalUsername,
        ...(invitationCode && { invitationCode }), // 🎫 Nur hinzufügen wenn vorhanden
        privacyAccepted: privacyAccepted === true // Backend setzt daraus privacyAcceptedAt
      };



      const response = await apiFetch('/api/v1/auth/register', {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include',  // Wichtig für Cookies
        body: JSON.stringify(requestData)
      });

      // Verbesserte Fehlerbehandlung
      if (!response.ok) {
        let errorMessage = 'Registrierung fehlgeschlagen';
        
        try {
          // Versuche JSON-Antwort zu parsen
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            console.error('Registration failed:', errorData);
            errorMessage = errorData.error || errorData.message || `Server-Fehler (${response.status})`;
          } else {
            // Nicht-JSON-Antwort
            const textResponse = await response.text();
            console.error('Non-JSON registration error:', textResponse);
            errorMessage = serverUnavailableMessage(response.status);
          }
        } catch (parseError) {
          console.error('Fehler beim Parsen der Server-Antwort:', parseError);
          errorMessage = `Server-Fehler (${response.status}): Antwort konnte nicht verarbeitet werden`;
        }
        
        throw new Error(errorMessage);
      }

      // Erfolgreiche Antwort verarbeiten
      let data;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          // Fallback für non-JSON Erfolgsantworten
          const textResponse = await response.text();
          data = { message: 'Registrierung erfolgreich', user: null };
        }
      } catch (jsonError) {
        console.error('Fehler beim Parsen der Erfolgsantwort:', jsonError);
        // Bei Parsing-Fehlern trotzdem als Erfolg werten (FusionAuth gibt manchmal leere Responses)
        data = { message: 'Registrierung erfolgreich', user: null };
      }


      // Flexiblere Antwort-Validierung
      if (!data || typeof data !== 'object') {
        console.warn('Unexpected response format, but registration likely successful');
        data = { message: 'Registrierung erfolgreich', user: null };
      }


      // Setze Token und User nach erfolgreicher Registrierung.
      // Das Backend antwortet bei fehlgeschlagenem Auto-Login mit 201 { user, message }
      // OHNE Token. Frueher blieb dieser Fall unbehandelt -> user gesetzt, token null.
      // Genau diese Kombination wirft der AuthErrorHandler in App.js hart auf /login.
      // Deshalb: ohne Token gar nicht erst ins Onboarding, sondern sauber zum Login.
      if (!data.token) {
        console.warn('Registrierung ohne Auto-Login-Token - User muss sich anmelden');
        return {
          success: true,
          requiresLogin: true,
          requiresProfileSetup: false,
          message: data.message || 'Registrierung erfolgreich! Bitte melde dich jetzt an.',
          user: data.user
        };
      }

      // Registrierung auf einem Geraet, auf dem noch ein anderes Konto angemeldet war
      // (Testgeraet, Klassen-iPad): Lernsession des vorherigen Nutzers verwerfen.
      const switchedAccount = handleUserSwitch(data.user?.id);

      localStorage.setItem('token', data.token);
      setAuthToken(data.token);
      if (switchedAccount) reassignPushDevice();

      if (data.user) {
        // Speichere die kompletten Benutzerdaten
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('userId', data.user.id);
        // Speichere die School-ID separat
        if (data.user.schoolId) {
          localStorage.setItem('schoolId', data.user.schoolId);
        }
        setUserWithAura(data.user);
        setShowProfileSetup(true);
      }

      return {
        success: true,
        requiresProfileSetup: true,
        message: data.message,
        user: data.user
      };

    } catch (error) {
      // Den Error selbst loggen: Ein nachgebautes { name, message, stack } kam in
      // Sentry nur als "[object keys: name,message,stack]" an.
      console.error('Registration error:', error);
      throw error;
    }
  };

  // Login - separate Funktion für die Anmeldung
  const login = async (email, password, rememberMe = false) => {
    try {
      
      const response = await apiFetch('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });

      // JSON nur abgesichert parsen: response.json() warf bei einer HTML-Fehlerseite
      // einen SyntaxError, und dessen Text stand im Anmeldeformular.
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!data || response.status >= 500) {
        throw new Error(serverUnavailableMessage(response.status));
      }

      if (!response.ok) {
        // warn statt error: Der Grund landet über den geworfenen Error ohnehin in
        // 'Login error:' (unten) und damit lesbar in Sentry bzw. im Noise-Filter.
        console.warn('Login failed:', response.status);
        throw new Error(data.error || 'Anmeldung fehlgeschlagen');
      }

      // Token dekodieren und User-ID extrahieren
      try {
        // decodeJwtPayload ist base64URL-fest (atob wirft sonst bei '-'/'_' im
        // Payload -> Login schlug fuer betroffene Tokens faelschlich fehl).
        const tokenPayload = decodeJwtPayload(data.token);
        if (!tokenPayload) {
          throw new Error('Ungültiges Token-Format');
        }

        // ✅ KORREKT: SchoolId kommt aus JWT Token (von FusionAuth)
        const schoolId = tokenPayload.schoolId || tokenPayload.tid || data.user.schoolId;

        // Demo-Modus Detection basierend auf SchoolId, nicht E-Mail
        const isDemo = schoolId === 'r7oeeymdsbms2cyzipwrau7h';
        
        // Stelle sicher, dass wir die User-ID aus dem Token verwenden
        const userId = tokenPayload.sub;
        
        // Anderes Konto als zuvor auf diesem Geraet? Dann die gespeicherte Lernsession
        // des vorherigen Nutzers verwerfen (sonst Heartbeat-404-Toasts, siehe utils/auth).
        const switchedAccount = handleUserSwitch(userId);

        // WICHTIG: Setze den Token sofort im State und localStorage
        localStorage.removeItem('token'); // Erst löschen, um sicherzustellen, dass es keine Probleme gibt
        localStorage.setItem('token', data.token);
        setAuthToken(data.token);
        if (switchedAccount) reassignPushDevice();
        
        // Debug-Überprüfung - Token gesetzt
        
        // NEU: Für Demo-Nutzer setzen wir den Demo-Modus
        if (isDemo) {
          localStorage.setItem('isDemo', 'true');
        } else {
          localStorage.removeItem('isDemo');
        }
        
        // Setze einen Validierungs-Marker für das Debugging
        localStorage.setItem('token_set_time', Date.now().toString());
        localStorage.setItem('token_check', 'valid_after_login');

        // Erweitere die Benutzerdaten mit wichtigen Basis-Daten
        const enhancedUser = {
          ...data.user,
          id: userId,
          schoolId: schoolId,
          isDemo: isDemo
        };
        
        // Speichere die erweiterten Benutzerdaten
        localStorage.setItem('userId', userId);
        localStorage.setItem('schoolId', schoolId);
        
        // Speichere auch das komplette Benutzer-Objekt als Fallback
        localStorage.setItem('user', JSON.stringify(enhancedUser));

        let finalUser = enhancedUser;
        let hasFreshUserData = false;

        // Beim ersten Login sofort vollständige Userdaten laden (inkl. AuraStats/Streak)
        try {
          const refreshedUser = await getUser(true);
          if (refreshedUser) {
            finalUser = refreshedUser;
            hasFreshUserData = true;
          }
        } catch (refreshError) {
          console.warn('Login: Konnte vollständige User-Daten nicht sofort laden:', refreshError);
        }

        // Fallback: Wenn /auth/me nicht sofort verfügbar ist, nutze Login-Response
        if (!hasFreshUserData) {
          setUserWithAura(enhancedUser);
        }

        setIsAuthenticated(true);
        setSentryUser(enhancedUser);

        // Onboarding noetig? Wenn /auth/me frisch geantwortet hat, gilt NUR diese Antwort:
        // /me kennt die Rolle (Admins bekommen kein Schüler-Profil-Setup), die Login-Antwort
        // ist nur der Fallback. Vorher ueberschrieb die Login-Antwort hier das korrekte
        // "false" von /me und schickte Schul-Admins ins Profil-Setup.
        const requiresProfileSetup = hasFreshUserData
          ? finalUser.requiresProfileSetup === true
          : !!data.requiresProfileSetup;
        if (requiresProfileSetup) {
          setShowProfileSetup(true);
        }

        return {
          success: true,
          token: data.token,
          user: finalUser,
          requiresProfileSetup,
          message: 'Anmeldung erfolgreich'
        };
      } catch (tokenError) {
        console.error('Token decode error:', tokenError);
        throw new Error('Token konnte nicht dekodiert werden');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // LTI-spezifische Login-Funktion
  const ltiLogin = async (ltiToken) => {
    try {
      // Token validieren
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/v1/auth/validate-lti-token`, { 
        token: ltiToken 
      });
      
      const { token, user, contextId } = response.data;
      
      // Token und Kontextdaten speichern
      localStorage.setItem('token', token);
      if (contextId) {
        localStorage.setItem('ltiContextId', contextId);
        setLtiContext(contextId);
        setIsLtiUser(true);
      }
      
      setUser(user);
      setIsAuthenticated(true);
      setSentryUser(user);

      return user;
    } catch (error) {
      console.error('LTI login failed', error);
      throw error;
    }
  };

  // Logout - separate Funktion für die Abmeldung
  const logout = () => {
    // Gepufferte Stöber-Ereignisse jetzt senden, solange das Token noch gilt. Sonst
    // gingen sie bis zu 2 s später mit dem Token des NÄCHSTEN Kontos raus (Prüferfund).
    try { flushBrowseEvents(); } catch (e) { /* nie den Logout blockieren */ }
    // Push-Token im Backend deaktivieren und Listener lösen, BEVOR das Auth-Token
    // weg ist — sonst bekommt das nächste Konto auf diesem Gerät die Pushes des vorigen.
    // Fire-and-forget: der Logout darf nicht am Netz hängen. Dynamischer Import,
    // damit der AuthContext nicht statisch am axios-Client hängt (Tests mocken axios).
    // State als Rückfall: Hat ein Aufrufer den localStorage schon vorher geleert (so
    // war es bis Sep 2026 in den Kontoeinstellungen), ginge der Request sonst ohne
    // Token raus, bekäme 401, und das Gerät bekäme weiter die Pushes dieses Kontos.
    const tokenForUnregister = localStorage.getItem('token') || authToken;
    import('../services/PushNotificationService')
      .then(({ default: pushService }) => {
        // Erst lokal abräumen (setzt den Zustand sofort zurück), dann abmelden. Lief
        // cleanup() erst nach dem Request (im langsamen Schul-WLAN Sekunden), kam ein
        // schneller Login des nächsten Kontos nur bis "Already initialized" und blieb
        // bis zum Kaltstart ohne Push (Code-Review 22.10.2026). Die Abmeldung betrifft
        // nur dieses Konto, eine neue Registrierung des nächsten bleibt davon unberührt.
        const cleanupDone = Promise.resolve(pushService.cleanup()).catch(() => {});
        return Promise.resolve(pushService.unregisterFromBackend(tokenForUnregister))
          .catch(() => {})
          .finally(() => cleanupDone);
      })
      .catch(() => {});

    // Alle Auth-relevanten localStorage/sessionStorage Keys löschen (inkl. userId, schoolId, user)
    clearAuthStorage();
    clearSentryUser();

    // Gecachte Daten (Sessions, Profile etc.) löschen, damit der nächste User
    // auf demselben Gerät keine fremden Daten aus dem Cache sieht
    browserCacheService.clear();

    // In-Memory State vollständig zurücksetzen
    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setLtiContext(null);
    setIsLtiUser(false);

    // Prüfen, ob wir in einer LTI-Sitzung sind
    const inLtiFrame = window.parent && window.parent !== window;

    // Wenn wir in einem LTI-iFrame sind, LMS informieren
    if (inLtiFrame) {
      try {
        window.parent.postMessage({ action: 'lti-logout' }, '*');
      } catch (e) {
        console.error('Fehler beim Senden der Logout-Nachricht an das LMS:', e);
      }
    }
  };

  /**
   * Aktualisiert das Benutzerprofil eines angemeldeten Benutzers
   * @param {Object} profileData - Die aktualisierten Profilinformationen
   * @returns {Promise<Object>} - Das aktualisierte Benutzerobjekt
   */
  const updateUserProfile = async (profileData) => {
    try {
      
      // Validiere, dass ein User angemeldet ist
      if (!user || !user.id) {
        throw new Error('Kein angemeldeter Benutzer gefunden');
      }

      // localStorage zuerst: Stille Token-Erneuerungen (axios-Interceptor, tokenRefresh)
      // schreiben nur dorthin, der authToken-State kann ein abgelaufenes Token halten.
      const currentToken = localStorage.getItem('token') || authToken;

      // Suche die Profil-ID aus verschiedenen möglichen Quellen
      let profileId = profileData?.id || 
                       (profileData.profile && profileData.profile.id) || 
                       (user.profile && user.profile.id) || 
                       user.profileId;
      
      // Validiere ProfileId
      if (!profileId) {
        console.warn('Keine Profil-ID gefunden, versuche User-ID als Fallback');
        // Als Fallback können wir versuchen, das Profil über die User-ID zu finden
        try {
          const schoolId = user.schoolId || localStorage.getItem('schoolId');
          
          if (!currentToken || !schoolId) {
            throw new Error('Token oder SchoolId fehlt');
          }
          
          // Versuche das Profil über die User-ID zu finden
          const profileResponse = await apiFetchWithToken(
            `/api/v1/profile/user/${user.id}`, 
            currentToken,
            {
              headers: {
                'X-School-ID': schoolId,
                'X-User-ID': user.id
              }
            }
          );
          
          if (profileResponse.ok) {
            const foundProfile = await profileResponse.json();
            if (foundProfile && foundProfile.id) {
              profileId = foundProfile.id;
            } else {
              throw new Error('Kein Profil für den Benutzer gefunden');
            }
          } else {
            throw new Error('Konnte Profil nicht über User-ID abrufen');
          }
        } catch (error) {
          console.error('Fehler beim Abrufen des Profils:', error);
          throw new Error('Keine Profil-ID gefunden und Fallback fehlgeschlagen');
        }
      }
      
      // Validiere Token
      if (!currentToken) {
        throw new Error('Kein Authentifizierungs-Token gefunden');
      }

      // Hole SchoolId für den Header
      const schoolId = user.schoolId || localStorage.getItem('schoolId');
      if (!schoolId) {
        throw new Error('Keine School-ID gefunden');
      }

      // Werte für Name extrahieren und normalisieren für bessere Kompatibilität
      // Wir prüfen verschiedene mögliche Quellen
      const nameField = profileData.name || profileData.displayName || 
                        (user.profile && user.profile.name) || 
                        (user.profile && user.profile.displayName) || 
                        user.name || user.displayName || '';

      // Extrahiere die Skills-Arrays und stelle sicher, dass keine Duplikate existieren
      // WICHTIG: Wenn keine Skills übergeben werden, auf undefined setzen (nicht leeres Array!)
      // So werden existierende Skills nicht überschrieben
      const offers = Array.isArray(profileData.offers)
        ? [...new Set(profileData.offers.map(skill =>
            typeof skill === 'string' ? skill : skill.name || ''
          ).filter(Boolean))]
        : undefined;  // undefined = nicht überschreiben

      const seeks = Array.isArray(profileData.seeks)
        ? [...new Set(profileData.seeks.map(skill =>
            typeof skill === 'string' ? skill : skill.name || ''
          ).filter(Boolean))]
        : undefined;  // undefined = nicht überschreiben

      const bioField = profileData.aboutMe || profileData.bio || 
                       (user.profile && user.profile.bio) || user.aboutMe || '';
      // Wenn image explizit auf null gesetzt wurde (Bild gelöscht), null beibehalten
      const imageExplicitlyCleared = 'image' in profileData && profileData.image === null;
      const imageField = imageExplicitlyCleared
        ? null
        : stripBackendUrl(
          profileData.image || profileData.avatar ||
          (user.profile && user.profile.avatar) || user.image || ''
        );

      // 🚀 RADICAL SIMPLIFICATION: NO CONVERSION NEEDED!

      // Stelle sicher, dass alle benötigten Felder im API-Objekt vorhanden sind
      // WICHTIG: Skills und availability nur senden wenn sie explizit übergeben wurden
      const apiProfileData = {
        name: nameField,
        aboutMe: bioField,
        bio: bioField, // Beide Feldnamen senden für Kompatibilität
        age: profileData.age || user.age || null,
        image: imageField,
        avatar: imageField, // Beide Feldnamen senden für Kompatibilität
        // Stelle sicher, dass das grade-Feld mitgesendet wird
        grade: profileData.grade || user.grade || '',
        // Availability nur senden wenn explizit übergeben - verhindert Überschreiben mit {}
        ...(profileData.availability !== undefined && { availability: profileData.availability }),
        // Skills nur senden wenn explizit übergeben - verhindert Überschreiben mit []
        ...(offers !== undefined && { offers }),
        ...(seeks !== undefined && { seeks })
      };


      const response = await apiFetchWithToken(`/api/v1/profile/${profileId}`, currentToken, {
        method: 'PUT',
        headers: {
          'X-School-ID': schoolId,
          'X-User-ID': user.id
        },
        body: JSON.stringify(apiProfileData)
      });

      // Handling für nicht erfolgreiche Antworten
      if (!response.ok) {
        let errorMessage = `Server antwortet mit Status ${response.status}`;
        
        // Verbesserte Fehlerbehandlung für verschiedene Antwortformate
        try {
          // Prüfe den Content-Type der Antwort
          const contentType = response.headers.get('content-type');
          
          if (contentType && contentType.includes('application/json')) {
            // JSON-Antwort verarbeiten
            const errorData = await response.json();
            console.error('❌ Server-Fehler:', errorData);
            
            // Verbesserte Fehlerextrahierung für JSON-Antworten
            if (errorData.error) {
              // Strukturierter Fehler mit error-Objekt
              if (typeof errorData.error === 'object') {
                const { message, code, details } = errorData.error;
                errorMessage = message || 'Unbekannter Fehler';
                
                if (code) {
                  errorMessage = `${errorMessage} (Code: ${code})`;
                }
                
                if (details) {
                  if (typeof details === 'string') {
                    errorMessage = `${errorMessage} - ${details}`;
                  } else if (typeof details === 'object') {
                    errorMessage = `${errorMessage} - ${JSON.stringify(details)}`;
                  }
                }
              } else if (typeof errorData.error === 'string') {
                errorMessage = errorData.error;
              }
            } else if (errorData.message) {
              // Einfacher Fehler mit nur message-Feld
              errorMessage = errorData.message;
            }
            
            // Wenn es Validierungsfehler gibt, zeige diese an
            if (errorData.details && Array.isArray(errorData.details)) {
              const detailMessages = errorData.details.map(d => `${d.field}: ${d.message}`).join('; ');
              errorMessage += ` (${detailMessages})`;
            }
          } else {
            // Nicht-JSON-Antwort (z.B. HTML) als Text verarbeiten
            const errorText = await response.text();
            console.error('❌ Server-Fehler (nicht-JSON):', errorText.substring(0, 500) + '...');
            
            // Erweiterte Logging-Informationen
            console.error('❌ Server-Antwort Details:', {
              status: response.status,
              statusText: response.statusText,
              contentType: contentType,
              textLänge: errorText.length,
              headers: Object.fromEntries([...response.headers.entries()])
            });
            
            // In der Fehlermeldung erwähnen, dass der Server eine nicht-JSON-Antwort geschickt hat
            errorMessage = `${errorMessage} (Server antwortete mit ${contentType || 'unbekanntem Format'})`;
          }
        } catch (e) {
          console.error('❌ Konnte Fehlerantwort nicht parsen:', e);
          errorMessage += ' (Antwortformat nicht erkennbar)';
        }
        
        throw new Error(`Fehler beim Aktualisieren des Profils: ${errorMessage}`);
      }

      // Erfolgreiche Antwort verarbeiten
      const responseData = await response.json();
      
      // Extrahiere das Profil aus der Antwort
      const updatedProfileData = responseData.profile || responseData;

      // Konsistentes User-Objekt erstellen
      const updatedUser = {
        ...user,
        // Basisdaten updaten
        name: nameField,
        displayName: nameField,
        age: apiProfileData.age,
        // 🚀 CRITICAL FIX: Availability hinzufügen!
        availability: apiProfileData.availability || updatedProfileData.availability || {},
        // Profil als separates Objekt
        profile: {
          ...user.profile,
          ...updatedProfileData,
          bio: bioField,
          avatar: imageField,
          // Auch in profile verfügbar machen
          availability: apiProfileData.availability || updatedProfileData.availability || {},
        },
        // Skills als einfache Arrays (für Kompatibilität mit Frontend-Komponenten)
        offers: offers,
        seeks: seeks,
        // Schachtelungen auflösen und direkte Eigenschaften hinzufügen
        offeredSkills: updatedProfileData.offeredSkills || [],
        soughtSkills: updatedProfileData.soughtSkills || [],
        auraLevel: updatedProfileData.auraStats?.level || user.auraLevel || 1,
        auraPoints: updatedProfileData.auraStats?.currentPoints || 
                    (user.profile?.auraStats?.currentPoints) || 
                    user.auraPoints || 0,
      };

      // Update State und localStorage
      setUserWithAura(updatedUser);

      return {
        success: true,
        data: updatedProfileData
      };
    } catch (error) {
      console.error('❌ Fehler beim Aktualisieren des Profils:', error);
      
      // Verbesserte Fehlerbehandlung für verschiedene Fehlertypen
      let errorMessage = error.message || 'Unbekannter Fehler';
      
      // Prüfe auf [object Object] in der Fehlermeldung
      if (error.message && error.message.includes('[object Object]')) {
        errorMessage = 'Serverfehler: Die Antwort konnte nicht richtig verarbeitet werden. Bitte kontaktiere den Support.';
        console.error('Fehler mit [object Object] in der Nachricht:', {
          originalMessage: error.message,
          error: error
        });
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  // Benutzerinformationen abrufen
  // ✅ FIX: Memoize getUser mit useCallback um Race Conditions zu verhindern
  // options.keepProfileSetup: Waehrend des laufenden Onboardings darf ein Refresh das
  // Flag NICHT auf false kippen (siehe Kommentar unten beim Abgleich).
  const getUser = useCallback(async (silent = false, options = {}) => {
    const keepProfileSetup = !!(options && options.keepProfileSetup);
    try {
      if (!silent) setLoading(true);
      // localStorage zuerst: Stille Token-Erneuerungen schreiben nur dorthin. Mit dem
      // State zuerst ging ein abgelaufenes Token an /me, der 401 löschte dann auch das
      // gültige Token (Zwangs-Logout). login() ruft getUser noch mit dem alten State
      // aus seiner Closure auf, bekommt über den localStorage aber das neue Token.
      const token = localStorage.getItem('token') || authToken;
      
      if (!token) {
        console.error('getUser: Kein Token vorhanden');
        setLoading(false);
        return null;
      }
      
      // Extrahiere Skills, unabhängig davon, ob sie als Arrays von Strings oder
      // Arrays von Objekten (mit name-Eigenschaft) oder different benannt vorliegen
      const extractSkills = (data, fieldName) => {
        if (!data) return [];

        // Prüfe zuerst das angegebene Feld
        const directField = data[fieldName];
        if (Array.isArray(directField) && directField.length > 0) {
          return directField.map(skill => typeof skill === 'string' ? skill : skill.name || '').filter(Boolean);
        }

        // Prüfe alternative Namenskonventionen (offeredSkills/soughtSkills)
        const altFieldMapping = {
          'offers': 'offeredSkills',
          'seeks': 'soughtSkills',
          'offeredSkills': 'offers',
          'soughtSkills': 'seeks'
        };
        
        const altField = data[altFieldMapping[fieldName]];
        if (Array.isArray(altField) && altField.length > 0) {
          return altField.map(skill => typeof skill === 'string' ? skill : skill.name || '').filter(Boolean);
        }
        
        // Falls wir hier landen, check ob wir ein profile.profile haben
        if (data.profile && data.profile[fieldName]) {
          return extractSkills(data.profile, fieldName);
        }
        
        // Nichts gefunden, leeres Array zurückgeben
        return [];
      };
      
      // Profil-ID und School-ID aus localStorage holen, falls vorhanden
      const storedUser = localStorage.getItem('user') ? 
        JSON.parse(localStorage.getItem('user')) : null;
      
      const schoolId = localStorage.getItem('schoolId') || 
                       (storedUser && storedUser.schoolId) || 
                       null;
      
      if (!schoolId) {
        console.warn('getUser: Keine School-ID verfügbar');
      }
      
      // User-Anfrage mit Retry-Mechanismus
      let response;
      const maxRetries = 2;
      let retryCount = 0;
      
      while (retryCount <= maxRetries) {
        try {
          response = await apiFetchWithToken(`/api/v1/auth/me`, token, {
            headers: {
              'X-School-ID': schoolId || '',
              'X-User-ID': storedUser?.id || '',
            },
          });
          
          if (response.ok) break; // Erfolgreiche Antwort, beende die Schleife
          
          // Bei 401 oder 403 Fehlern nicht erneut versuchen
          if (response.status === 401 || response.status === 403) {
            console.error(`getUser: Authentifizierungsfehler: ${response.status}`);
            break;
          }
          
          retryCount++;
          if (retryCount <= maxRetries) {
            console.warn(`getUser: Wiederhole Anfrage nach Fehler (${retryCount}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Warte 1 Sekunde vor erneuter Anfrage
          }
        } catch (fetchError) {
          console.error(`getUser: Netzwerkfehler beim Abrufen der Benutzerdaten:`, fetchError);
          retryCount++;
          if (retryCount <= maxRetries) {
            console.warn(`getUser: Wiederhole Anfrage nach Netzwerkfehler (${retryCount}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Warte 1 Sekunde vor erneuter Anfrage
          } else {
            setLoading(false);
            throw new Error('Netzwerkfehler beim Abrufen der Benutzerdaten');
          }
        }
      }

      if (!response || !response.ok) {
        console.error(`getUser: Fehler bei der Anfrage: ${response?.status} ${response?.statusText}`);
        if (response?.status === 401) {
          // Token ungültig oder abgelaufen – alle Auth-Daten bereinigen
          clearAuthStorage();
          setAuthToken(null);
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return null;
        }
        
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || `Fehler ${response.status} beim Abrufen des Benutzers`);
        } catch (parseError) {
          throw new Error(`Fehler ${response.status} beim Abrufen des Benutzers`);
        }
      }

      const userData = await response.json();

      // Die Tenant-Security-Middleware im Backend beantwortet /auth/me im Fehlerfall mit
      // HTTP 200 und einem Fehler-Body (bzw. null) statt mit 4xx/5xx. Ohne diese Pruefung
      // wuerde daraus ein "User" ohne id -> stiller Logout ohne sichtbaren 401.
      // Bewusst KEIN clearAuthStorage: der Token ist ja gueltig, nur die Antwort ist kaputt.
      if (!userData || !userData.id) {
        console.error('getUser: Antwort ohne User-ID erhalten (Backend-Fehler mit Status 200?)', userData);
        setLoading(false);
        throw new Error('Benutzerdaten konnten nicht geladen werden');
      }

      // Während der Anfrage abgemeldet? Dann gilt die Antwort nicht mehr. Sonst schrieb
      // setUserWithAura das Konto ohne Token zurück in den Speicher - genau der Rest,
      // den der nächste Start für eine Anmeldung hielt (Sentry NODE-P, Okt 2026).
      if (!localStorage.getItem('token')) {
        setLoading(false);
        return null;
      }

      // Onboarding-Flag mit dem Server abgleichen - bewusst nur in EINE Richtung.
      // Ein Hintergrund-Refresh darf niemanden mitten in der Session ins Onboarding werfen;
      // ohne dieses Zuruecksetzen blieb showProfileSetup dagegen dauerhaft haengen.
      // AUSNAHME keepProfileSetup: App.js ruft getUser direkt nach dem Speichern im
      // Onboarding auf. Kippt das Flag dort auf false, wechselt App.js von der
      // Setup-Ansicht zurueck auf die Routen -> ProfileSetup wird neu gemountet ->
      // Schritt 0 ("Willkommen bei EachOtter!") mit leeren Feldern, obwohl das Profil
      // laengst in der DB liegt. Genau das war der "man muss wieder von vorne anfangen"-Bug
      // (Prod-Trace 1. Sep 2026, 19:26 UTC). Das Flag setzt handleProfileSetupFinish zurueck.
      if (!userData.requiresProfileSetup && !keepProfileSetup) {
        setShowProfileSetup(false);
      }

      // Verbesserte Deduplizierungsfunktion für Skills
      const deduplicateSkills = (skills) => {
        // Wenn keine Skills oder kein Array, leeres Array zurückgeben
        if (!skills || !Array.isArray(skills)) return [];
        
        // Effektive case-insensitive Deduplizierung
        const uniqueSkills = [];
        const lowerCaseMap = new Map(); // Map speichert Original-Fall für jeden Skill
        
        // Iteriere durch alle Skills
        for (const skill of skills) {
          // Ignoriere null/undefined/leere Skills
          if (!skill) continue;
          
          // Normalisiere den Skill-String
          const skillString = typeof skill === 'string' ? skill : 
                             (typeof skill === 'object' && skill.name) ? skill.name : '';
          
          // Ignoriere leere Strings
          if (!skillString.trim()) continue;
          
          // Konvertiere zu Kleinbuchstaben für den Vergleich
          const skillLower = skillString.toLowerCase().trim();
          
          // Wenn wir diesen Skill (case-insensitive) noch nicht haben, füge ihn hinzu
          if (!lowerCaseMap.has(skillLower)) {
            lowerCaseMap.set(skillLower, skillString);
            uniqueSkills.push(skillString);
          }
        }
        
        return uniqueSkills;
      };
      
      // **************************************************************************
      // VERBESSERTE DATENEXTRAKTION FÜR BENUTZER/PROFIL DATEN
      // **************************************************************************
      
      // WICHTIG: Extrahiere und analysiere alle verschachtelten Strukturen
      
      // Prüfe zuerst, ob ein korrektes Profil-Objekt zurückgegeben wurde
      // Dies kann entweder ein Profil-Feld im Benutzerobjekt oder ein direktes Profil sein
      const profile = userData.profile || {};
      
      // Die Benutzerdaten können verschiedene Struktur haben (je nach Endpunkt/API-Antwort)
      // 1. Als direktes Objekt, wo die Attribute direkt zugreifbar sind
      // 2. Als verschachteltes Objekt, mit 'profile' und 'user' Feldern
      // 3. Als kombiniertes Objekt mit einigen direkten Feldern und einem 'profile'-Objekt
      
      
      // Extrahiere Benutzer-ID
      const userId = userData.id || userData.userId || (userData.user && userData.user.id);
      
      // Extrahiere E-Mail (kann in verschiedenen Strukturen liegen)
      const email = userData.email || (userData.user && userData.user.email);
      
      // Extrahiere User-Rolle (kann in verschiedenen Strukturen liegen)
      const userRole = userData.role || 
                      (userData.user && userData.user.role) || 
                      'STUDENT'; // Standardwert, wenn keine Rolle gefunden wird
      
      // Aktualisiere School-ID (kann in verschiedenen Strukturen liegen)
      let updatedSchoolId = userData.schoolId || 
                (userData.user && userData.user.schoolId) || 
                profile.schoolId || schoolId; // Behalte den vorherigen Wert als Fallback
      
      // Extrahiere den Namen mit mehreren Fallbacks
      const nameField = userData.name || 
                       userData.displayName || 
                       (userData.user && (userData.user.name || userData.user.displayName)) ||
                       profile.name ||
                       profile.displayName ||
                       email?.split('@')[0] || 
                       'User';
      
      // Extrahiere Bio/AboutMe mit mehreren Fallbacks
      const bioField = userData.bio || 
                       userData.aboutMe || 
                       profile.bio || 
                       profile.aboutMe || 
                       '';
      
      // Extrahiere Profilbild mit mehreren Fallbacks
      const imageField = userData.image || 
                         userData.avatar || 
                         profile.avatar || 
                         profile.image || 
                         '';
      
      // Extrahiere Alter mit mehreren Fallbacks
      const ageField = userData.age || 
                      profile.age || 
                      (userData.user && userData.user.age) || 
                      null;
      
      // Extrahiere Geschlecht mit mehreren Fallbacks  
      const genderField = userData.gender || 
                         profile.gender || 
                         (userData.user && userData.user.gender) || 
                         '';
      
      // Extrahiere angebotene Skills (offeredSkills oder offers)
      let offeredSkills = [];
      
      // Versuch 1: Direkt im Hauptobjekt unter 'offers'
      if (userData.offers && Array.isArray(userData.offers)) {
        offeredSkills = userData.offers;
      } 
      // Versuch 2: Direkt im Hauptobjekt unter 'offeredSkills'
      else if (userData.offeredSkills && Array.isArray(userData.offeredSkills)) {
        offeredSkills = userData.offeredSkills;
      }
      // Versuch 3: Im profile-Objekt unter 'offers'
      else if (profile.offers && Array.isArray(profile.offers)) {
        offeredSkills = profile.offers;
      }
      // Versuch 4: Im profile-Objekt unter 'offeredSkills'
      else if (profile.offeredSkills && Array.isArray(profile.offeredSkills)) {
        offeredSkills = profile.offeredSkills;
      }
      
      // Extrahiere gesuchte Skills (soughtSkills oder seeks)
      let soughtSkills = [];
      
      // Versuch 1: Direkt im Hauptobjekt unter 'seeks'
      if (userData.seeks && Array.isArray(userData.seeks)) {
        soughtSkills = userData.seeks;
      } 
      // Versuch 2: Direkt im Hauptobjekt unter 'soughtSkills'
      else if (userData.soughtSkills && Array.isArray(userData.soughtSkills)) {
        soughtSkills = userData.soughtSkills;
      }
      // Versuch 3: Im profile-Objekt unter 'seeks'
      else if (profile.seeks && Array.isArray(profile.seeks)) {
        soughtSkills = profile.seeks;
      }
      // Versuch 4: Im profile-Objekt unter 'soughtSkills'
      else if (profile.soughtSkills && Array.isArray(profile.soughtSkills)) {
        soughtSkills = profile.soughtSkills;
      }
      
      // Konvertiere Skills von Objekten zu Strings, falls nötig, und dedupliziere
      const processedOffers = deduplicateSkills(offeredSkills.map(skill => 
        typeof skill === 'string' ? skill : 
        (typeof skill === 'object' && skill.name) ? skill.name : ''
      ).filter(Boolean));
      
      const processedSeeks = deduplicateSkills(soughtSkills.map(skill => 
        typeof skill === 'string' ? skill : 
        (typeof skill === 'object' && skill.name) ? skill.name : ''
      ).filter(Boolean));
      
      // Extrahiere Verfügbarkeit
      const availabilityField = userData.availability || 
                               profile.availability || 
                               {};
      
      // Aura-Statistiken
      const auraStats = profile.auraStats || userData.auraStats || {};
      
      // Debug-Log - User-Daten verarbeitet
      
      // Erstelle ein konsistentes Benutzer-Objekt mit allen Daten
      const enhancedUserData = {
        // Basis-Benutzer-Daten aus verschiedenen Quellen zusammengeführt
        ...userData,
        
        // Normalisierte Hauptfelder (für einfachen Zugriff im Frontend)
        id: userId,
        email: email,
        name: nameField,
        displayName: nameField,
        bio: bioField,
        aboutMe: bioField,
        image: imageField,
        avatar: imageField,
        age: ageField,
        gender: genderField,
        verified: userData.verified || false,
        schoolId: updatedSchoolId,
        role: userRole, // Benutzerrolle für Berechtigungsprüfungen
        
        // Verfügbarkeitszeiten
        availability: availabilityField,
        
        // Deduplizierte Skills-Arrays
        offers: processedOffers,
        seeks: processedSeeks,
        
        // Referenzen auf die original-Skills-Arrays (können Objekte sein)
        offeredSkills: offeredSkills,
        soughtSkills: soughtSkills,
        
        // Profile-Objekt mit vollständigen Daten
        profile: {
          ...profile,
          id: profile.id || userId,
          userId: userId,
          schoolId: updatedSchoolId,
          name: nameField,
          displayName: nameField,
          bio: bioField,
          aboutMe: bioField,
          avatar: imageField,
          image: imageField,
          age: ageField,
          gender: genderField,
          offers: processedOffers,
          seeks: processedSeeks,
          offeredSkills: offeredSkills,
          soughtSkills: soughtSkills,
          availability: availabilityField
        },
        
        // Aura-Daten
        auraLevel: auraStats.level || 1,
        auraPoints: auraStats.currentPoints || 0,
        auraStats: auraStats,
        
        // Zusätzliche Metadaten
        _source: 'getUser API call',
        _timestamp: new Date().toISOString()
      };
      
      // User-Status aktualisieren
      setUserWithAura(enhancedUserData);
      
      // Im LocalStorage speichern
      localStorage.setItem('user', JSON.stringify(enhancedUserData));
      
      if (enhancedUserData.schoolId) {
        localStorage.setItem('schoolId', enhancedUserData.schoolId);
      }
      
      // Speichere den Zeitpunkt des letzten Aufrufs
      localStorage.setItem('lastUserFetch', Date.now().toString());
      
      setLoading(false);
      return enhancedUserData;
    } catch (error) {
      console.error('getUser: Fehler:', error);
      setLoading(false);
      throw error;
    }
  }, [authToken, setUserWithAura]); // Dependencies: authToken und setUserWithAura

  // Funktion zum Überprüfen des Tokens (für Debugging)
  const checkToken = () => {
    const tokenFromState = authToken;
    const tokenFromStorage = localStorage.getItem('token');
    const tokenSetTime = localStorage.getItem('token_set_time');
    
    return {
      hasTokenInState: !!tokenFromState,
      hasTokenInStorage: !!tokenFromStorage,
      tokenAgeSeconds: tokenSetTime ? Math.floor((Date.now() - parseInt(tokenSetTime, 10)) / 1000) : null
    };
  };

  // Methode zum Überprüfen und Erneuern des Tokens, wenn er abgelaufen ist oder bald abläuft
  const checkAndRefreshToken = async () => {
    try {
      // Überprüfe, ob ein Token vorhanden ist
      const token = localStorage.getItem('token');
      if (!token) return false;

      try {
        // Token dekodieren und Expiration prüfen (base64URL-fest)
        const payload = decodeJwtPayload(token);
        if (!payload) return false;

        // Prüfe, ob der Token bald abläuft (in den nächsten 10 Minuten)
        const expiryTime = payload.exp * 1000; // In Millisekunden
        const currentTime = Date.now();
        const timeUntilExpiry = expiryTime - currentTime;
        
        // Wenn der Token bereits abgelaufen ist oder in weniger als 10 Minuten abläuft,
        // versuche ihn zu erneuern
        if (timeUntilExpiry < 600000) { // 10 Minuten in Millisekunden
          
          // Hier würde normalerweise ein API-Call zum Token-Refresh erfolgen
          // Da wir keine dedizierte Refresh-Methode haben, versuchen wir einen Neulogin mit Cookies
          // Dies ist ein einfacher Ansatz - in Produktionssystemen würde man einen dedizierten Token-Refresh implementieren
          
          const refreshResponse = await apiFetchWithToken(`/api/v1/auth/refresh`, token, {
            method: 'POST',
            credentials: 'include' // Wichtig für Cookies
          });
          
          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            
            if (refreshData.token) {
              // Token aktualisieren
              localStorage.setItem('token', refreshData.token);
              setAuthToken(refreshData.token);
              localStorage.setItem('token_set_time', Date.now().toString());
              return true;
            }
          } else {
            console.warn('Token-Erneuerung fehlgeschlagen, Benutzer muss sich erneut anmelden');
            // Bei Fehlschlag alte Token-Daten löschen
            logout();
            return false;
          }
        } else {
          // Token ist noch gültig
          return true;
        }
      } catch (decodeError) {
        console.error('Fehler beim Dekodieren des Tokens:', decodeError);
        return false;
      }
    } catch (error) {
      console.error('Fehler bei der Token-Überprüfung:', error);
      return false;
    }
    
    return false;
  };

  // Event-Listener für Token-Überprüfung
  useEffect(() => {
    const handleTokenCheck = async () => {
      await checkAndRefreshToken();
    };
    
    // Event-Listener hinzufügen
    window.addEventListener('auth:checkToken', handleTokenCheck);

    // Cleanup bei Component Unmount
    return () => {
      window.removeEventListener('auth:checkToken', handleTokenCheck);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Leere Dependency-Array mit Disable-Comment, da wir checkAndRefreshToken nicht als Dependency angeben wollen

  // Schullogo beim App-Start einmalig vorladen, sobald die schoolId bekannt ist.
  // So liegt das Ergebnis persistent im Cache und der Header zeigt das richtige
  // Logo schon beim ersten Render (kein Flackern, auch nach vollem Reload).
  useEffect(() => {
    const schoolId = user?.schoolId || localStorage.getItem('schoolId');
    // /schools/:id/logo verlangt eine Anmeldung. Ohne Token gibt es nur einen 401.
    if (schoolId && localStorage.getItem('token')) {
      // force: einmal pro Sitzung revalidieren (Header-Mounts nutzen danach den Cache).
      fetchAndCacheSchoolLogo(schoolId, { force: true }).catch(() => {});
    }
  }, [user?.schoolId]);

  // Frisches Token übernehmen, das der Server außerhalb von Login/Refresh ausstellt:
  // /auth/change-password widerruft alle älteren Tokens des Kontos (Sep 2026). State
  // UND localStorage - sonst schickt z.B. getUser das widerrufene Token, bekommt 401
  // und meldet die Person nach der eigenen Passwortänderung ab.
  const adoptToken = useCallback((newToken) => {
    if (!newToken) return;
    localStorage.setItem('token', newToken);
    localStorage.setItem('token_set_time', Date.now().toString());
    setAuthToken(newToken);
  }, []);

  // Performance: useMemo verhindert unnötige Re-Renders aller Consumer-Komponenten
  const contextValue = useMemo(() => ({
    user,
    token: authToken,
    authToken,
    showProfileSetup,
    setShowProfileSetup,
    register,
    login,
    ltiLogin,
    logout,
    updateUserProfile,
    getUser,
    checkToken,
    checkAndRefreshToken,
    adoptToken,
    isLtiUser,
    ltiContext,
    setUser,
    isAuthenticated,
    loading
  }), [user, authToken, showProfileSetup, isAuthenticated, loading, isLtiUser, ltiContext,
    setShowProfileSetup, register, login, ltiLogin, logout, updateUserProfile, getUser,
    checkToken, checkAndRefreshToken, adoptToken, setUser]);

  // Startsignal fuer den Startbildschirm (components/ui/LogoSplash.js).
  // Der Splash haengt ueber dem AuthProvider und kann `loading` deshalb nicht
  // per Context lesen — dieser Anstoss ist die Bruecke. Das Flag auf `window`
  // deckt den Fall ab, dass der Splash erst nach dem Ereignis mountet.
  useEffect(() => {
    if (loading) return;
    window.__eachotterAppReady = true;
    window.dispatchEvent(new Event('eachotter:app-ready'));
  }, [loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Rollenprüfungs-Funktionen
const hasRequiredRole = (userRole, requiredRoles) => {
  if (!userRole || !requiredRoles || requiredRoles.length === 0) return false;
  
  // Rollengewichtung für Berechtigungshierarchie
  const roleWeights = {
    'STUDENT': 1,
    'TEACHER': 2,
    'ADMIN': 3,
    'SUPERADMIN': 4
  };
  
  const userRoleWeight = roleWeights[userRole] || 0;
  
  // Prüfe, ob die Benutzerrolle ausreichend ist für eine der erforderlichen Rollen
  return requiredRoles.some(role => {
    const requiredWeight = roleWeights[role] || 0;
    return userRoleWeight >= requiredWeight;
  });
};

// Custom Hook für einfacheren Zugriff auf den Auth-Context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth muss innerhalb eines AuthProviders verwendet werden');
  }
  
  // Erweitere den Context um Rollenprüfungs-Funktionen
  return {
    ...context,
    // Prüft, ob der Benutzer eine bestimmte Rolle hat
    hasRole: (role) => hasRequiredRole(context.user?.role, [role]),
    // Prüft, ob der Benutzer eine von mehreren Rollen hat
    hasAnyRole: (roles) => hasRequiredRole(context.user?.role, roles),
    // Prüft, ob der Benutzer mindestens die angegebene Rolle hat (hierarchisch)
    hasMinRole: (minRole) => {
      const roleHierarchy = ['STUDENT', 'TEACHER', 'ADMIN', 'SUPERADMIN'];
      const userRoleIndex = roleHierarchy.indexOf(context.user?.role || 'STUDENT');
      const minRoleIndex = roleHierarchy.indexOf(minRole);
      return userRoleIndex >= minRoleIndex;
    },
    // Gibt die aktuelle Benutzerrolle zurück
    getUserRole: () => context.user?.role || 'STUDENT'
  };
};

export default AuthContext; 
